import React, { useEffect, useState, useRef } from 'react';
import { injectFonts, T } from '../utils/fonts';

const Aurora = () => {
  useEffect(() => {
    const id = 'aurora-kf';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @keyframes aurora1{0%{transform:translate(0,0) scale(1);opacity:.55}33%{transform:translate(8%,-12%) scale(1.15);opacity:.7}66%{transform:translate(-5%,8%) scale(.95);opacity:.5}100%{transform:translate(0,0) scale(1);opacity:.55}}
      @keyframes aurora2{0%{transform:translate(0,0) scale(1);opacity:.45}33%{transform:translate(-10%,6%) scale(1.1);opacity:.6}66%{transform:translate(7%,-9%) scale(1.05);opacity:.4}100%{transform:translate(0,0) scale(1);opacity:.45}}
      @keyframes aurora3{0%{transform:translate(0,0) scale(1.05);opacity:.4}50%{transform:translate(6%,10%) scale(.9);opacity:.6}100%{transform:translate(0,0) scale(1.05);opacity:.4}}
      @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
      @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
      @keyframes cardIn{from{opacity:0;transform:translateY(32px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
      @keyframes borderSlide{0%{background-position:200% 0}100%{background-position:-200% 0}}
    `;
    document.head.appendChild(s);
  }, []);
  return (
    <div style={{position:'fixed',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:0,filter:'blur(80px)'}}>
      <div style={{position:'absolute',width:'70vw',height:'70vw',maxWidth:900,maxHeight:900,borderRadius:'50%',background:'radial-gradient(circle,rgba(99,102,241,.55) 0%,rgba(139,92,246,.3) 50%,transparent 70%)',top:'-20%',left:'-10%',animation:'aurora1 14s ease-in-out infinite'}}/>
      <div style={{position:'absolute',width:'65vw',height:'65vw',maxWidth:800,maxHeight:800,borderRadius:'50%',background:'radial-gradient(circle,rgba(6,182,212,.5) 0%,rgba(16,185,129,.25) 50%,transparent 70%)',bottom:'-10%',right:'-10%',animation:'aurora2 18s ease-in-out infinite'}}/>
      <div style={{position:'absolute',width:'55vw',height:'55vw',maxWidth:700,maxHeight:700,borderRadius:'50%',background:'radial-gradient(circle,rgba(245,158,11,.35) 0%,rgba(239,68,68,.2) 50%,transparent 70%)',top:'30%',left:'35%',animation:'aurora3 22s ease-in-out infinite'}}/>
    </div>
  );
};

const SpotlightCard = ({ children, accentColor, animDelay, style }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x:'50%', y:'50%', visible:false });
  const onMove = (e) => { const r = ref.current.getBoundingClientRect(); setPos({ x:`${e.clientX-r.left}px`, y:`${e.clientY-r.top}px`, visible:true }); };
  const onLeave = () => setPos(p => ({ ...p, visible:false }));
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ position:'relative', animation:`cardIn 0.6s ${animDelay}s both cubic-bezier(0.4,0,0.2,1)`, ...style }}>
      <div style={{
        position:'absolute', inset:0, borderRadius:20,
        background:`radial-gradient(320px circle at ${pos.x} ${pos.y}, ${accentColor}28 0%, transparent 65%)`,
        opacity: pos.visible ? 1 : 0, transition:'opacity 0.35s ease',
        pointerEvents:'none', zIndex:3,
      }} />
      {children}
    </div>
  );
};

const Section = ({ title, children, last }) => (
  <section style={{ marginBottom:last?0:32, paddingBottom:last?0:32, borderBottom:last?'none':'1px solid rgba(255,255,255,0.07)' }}>
    <h2 style={{ fontSize:'clamp(1.05rem,4vw,1.5rem)', color:T.godot, fontWeight:700, margin:'0 0 14px', fontFamily: T.fontDisplay }}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p style={{ fontSize:'clamp(0.88rem,3.5vw,1rem)', color:T.muted, lineHeight:1.8, marginBottom:12, fontFamily: T.fontBody }}>{children}</p>
);

const AccessibilityStatement = () => {
  useEffect(() => { injectFonts(); }, []);

  const s = {
    page: { background:T.bg, minHeight:'100vh', padding:'clamp(16px,5vw,40px)', paddingTop:'clamp(80px,14vw,120px)', fontFamily:T.fontBody, position:'relative' },
    wrap: { maxWidth:900, margin:'0 auto', position:'relative', zIndex:1 },
    card: { background:T.panel, backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:`1px solid ${T.line}`, borderRadius:20, padding:'clamp(20px,5vw,48px)', boxShadow:'0 8px 40px rgba(0,0,0,0.4)', overflow:'hidden' },
    list: { paddingRight:20, listStyle:'disc', margin:'10px 0 12px' },
    li:   { fontSize:'clamp(0.88rem,3.5vw,1rem)', color:T.muted, lineHeight:1.8, marginBottom:8, fontFamily: T.fontBody },
    contactBox: { background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.25)', borderRadius:12, padding:'clamp(14px,3.5vw,22px)', marginTop:12 },
    link: { color:T.indigoLight, fontWeight:600, textDecoration:'none' },
  };

  return (
    <div style={s.page} dir="rtl">
      <Aurora />
      <div style={s.wrap}>
        <header style={{ textAlign:'center', marginBottom:40, animation:'fadeUp 0.6s 0.1s both' }}>
          <h1 style={{ fontSize:'clamp(1.8rem,7vw,3.2rem)', margin:'0 0 10px', fontWeight:700, letterSpacing:'-0.02em', fontFamily: T.fontDisplay, background:`linear-gradient(90deg,${T.text} 20%,#fff 40%,${T.muted} 60%,${T.text} 80%)`, backgroundSize:'200% auto', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', animation:'shimmer 3.5s linear infinite' }}>
            הצהרת נגישות
          </h1>
          <p style={{ fontSize:'clamp(0.8rem,3vw,0.9rem)', color:T.faint, fontFamily: T.fontBody }}>עדכון אחרון: {new Date().toLocaleDateString('he-IL')}</p>
        </header>

        <SpotlightCard accentColor="#10b981" animDelay={0.1}>
          <div style={s.card}>
            <div style={{
              height:3, width:'100%', marginBottom:24, borderRadius:'0 0 3px 3px',
              background:`linear-gradient(90deg, transparent 0%, #10b981 30%, #34d399 70%, transparent 100%)`,
              backgroundSize:'200% 100%', animation:'borderSlide 4s linear infinite',
            }} />
            <Section title="מבוא">
              <P>אנו ב-GoGameDev3D שמים חשיבות רבה במתן אפשרות שווה לאנשים עם מוגבלות לשימוש במידע המוצג באתר ולאפשר חווית גלישה טובה יותר.</P>
            </Section>
            <Section title="התקן והנחיות">
              <P>אנו שואפים לדבוק בהמלצות התקן הישראלי (ת"י 5568) ומסמך WCAG 2.1 ברמת AA.</P>
              <ul style={s.list}>
                <li style={s.li}><strong style={{color:T.text}}>ניתן לתפיסה (Perceivable):</strong> המידע ורכיבי הממשק חייבים להיות ניתנים להצגה בדרכים שהמשתמשים יכולים לתפוס</li>
                <li style={s.li}><strong style={{color:T.text}}>ניתן להפעלה (Operable):</strong> רכיבי הממשק והניווט חייבים להיות ניתנים להפעלה</li>
                <li style={s.li}><strong style={{color:T.text}}>ניתן להבנה (Understandable):</strong> המידע והפעלת הממשק חייבים להיות ברורים ומובנים</li>
              </ul>
            </Section>
            <Section title="אמצעי נגישות באתר">
              <ul style={s.list}>
                {[
                  ['ניווט במקלדת','ניתן לנווט באתר באמצעות מקלדת בלבד'],
                  ['תמיכה בקוראי מסך','האתר תואם ל-NVDA, JAWS ו-VoiceOver'],
                  ['ניגודיות צבעים','כל הטקסטים עומדים בסטנדרט ניגודיות WCAG AA'],
                  ['טקסט חלופי','כל התמונות כוללות Alt Text מפורט'],
                  ['מבנה סמנטי','שימוש במבנה HTML5 סמנטי עם כותרות מסודרות היררכית'],
                  ['גודל טקסט גמיש','האתר מאפשר הגדלת טקסט עד 200% ללא אובדן תוכן'],
                  ['דילוג לתוכן ראשי','קישור "דלג לתוכן הראשי" זמין למשתמשי מקלדת'],
                ].map(([t,d],i)=><li key={i} style={s.li}><strong style={{color:T.text}}>{t}:</strong> {d}</li>)}
              </ul>
            </Section>
            <Section title="פניה בנושאי נגישות">
              <P>אם נתקלתם בבעיית נגישות, צוות הנגישות שלנו עומד לרשותכם. אנו מתחייבים להגיב תוך 5 ימי עסקים.</P>
              <div style={s.contactBox}>
                <P><strong style={{color:T.text}}>מוקד טלפוני:</strong> 054-944-7314</P>
                <P><strong style={{color:T.text}}>דוא"ל:</strong> GoGameDev3D@gmail.com</P>
                <P style={{margin:0}}><strong style={{color:T.text}}>WhatsApp:</strong> <a href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer" style={s.link}>שלח הודעה</a></P>
              </div>
            </Section>
            <Section title="שיפורים עתידיים" last>
              <ul style={s.list}>{['שיפור נגישות התצוגות התלת-ממדיות','הוספת תיאורים מורחבים לתכנים מורכבים','בדיקות נגישות קבועות ועדכון התכנים'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
            </Section>
            <div style={{ marginTop:32, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.07)', textAlign:'center' }}>
              <p style={{ fontSize:'clamp(0.8rem,3vw,0.9rem)', color:T.faint, fontStyle:'italic', fontFamily: T.fontBody }}>
                <strong style={{color:T.godot}}>תאריך עדכון אחרון:</strong> {new Date().toLocaleDateString('he-IL',{year:'numeric',month:'long',day:'numeric'})}
              </p>
            </div>
          </div>
        </SpotlightCard>
      </div>
    </div>
  );
};

export default AccessibilityStatement;
